<template>
  <div class="hcl-toolbar">
    <slot name="toolbar">
      <div
        v-if="hasLeftContent"
        class="left"
      >
        <slot name="pre-left"></slot>
        <component
          v-for="(content, index) in props.definition.left"
          :is="component(content)"
          :key="index"
          v-on:hcl-button-clicked="onButtonClicked"
          v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
          :definition="content"
          :data="props.data"
          :config="props.config"
        >
        </component>
        <slot name="post-left"></slot>
      </div>
      <div
        v-if="hasMiddleContent"
        class="middle"
      >
        <slot name="pre-middle"></slot>
        <component
          v-for="(content, index) in props.definition.middle"
          :is="component(content)"
          :key="index"
          v-on:hcl-button-clicked="onButtonClicked"
          v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
          :definition="content"
          :data="props.data"
          :config="props.config"
        >
        </component>
        <slot name="post-middle"></slot>
      </div>
      <div
        v-if="hasRightContent"
        class="right"
      >
        <slot name="pre-right"></slot>
        <component
          v-for="(content, index) in props.definition.right"
          :is="component(content)"
          :key="index"
          v-on:hcl-button-clicked="onButtonClicked"
          v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
          :definition="content"
          :data="props.data"
          :config="props.config"
        >
        </component>
        <slot name="post-right"></slot>
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { provide } from 'vue'

import HclToolbarContentEmpty from '@/components/ToolbarContentEmpty/ToolbarContentEmpty.vue'
import HclToolbarContentHeader from '@/components/ToolbarContentHeader/ToolbarContentHeader.vue'
import HclToolbarContentActions from '@/components/ToolbarContentActions/ToolbarContentActions.vue'
import HclToolbarContentActionSelect from '@/components/ToolbarContentActionSelect/ToolbarContentActionSelect.vue'
import HclToolbarContentPager from '@/components/ToolbarContentPager/ToolbarContentPager.vue'
import HclToolbarContentListColumnSelector from '@/components/ToolbarContentListColumnSelector/ToolbarContentListColumnSelector.vue'

import type { DataToolbar, DefinitionListContentConfiguration, DefinitionToolbar, DefinitionToolbarContent, EventButtonClicked, EventListGlobalSelectAllChanged } from '@/components/types'

interface Props {
  /**
   * Definition as an JS object.
   */
  definition: DefinitionToolbar
  /**
   * Data as an JS object.
   */
  data?: DataToolbar
  /**
   * Configuration as an JS object.
   */
  config?: DefinitionListContentConfiguration
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined,
  data: undefined,
  config: undefined
})

provide('toolbarMetaId', props.definition.meta.id ?? '')

const hasLeftContent = (props.definition.left ?? []).length > 0
const hasMiddleContent = (props.definition.middle ?? []).length > 0
const hasRightContent = (props.definition.right ?? []).length > 0

const component = (definition: DefinitionToolbarContent) => {
  if (definition.type === 'action-select') return HclToolbarContentActionSelect
  if (definition.type === 'list-column-selector') return HclToolbarContentListColumnSelector
  if (definition.type === 'pager') return HclToolbarContentPager
  if (definition.type === 'empty') return HclToolbarContentEmpty
  if (definition.type === 'header') return HclToolbarContentHeader
  if (definition.type === 'actions') return HclToolbarContentActions

  return HclToolbarContentEmpty
}

const emit = defineEmits(['hcl-button-clicked', 'hcl-list-global-select-all-changed'])

const onButtonClicked = (event) => {
  emit('hcl-button-clicked', event)
}

const onListGlobalSelectAllChanged = (event) => {
  emit('hcl-list-global-select-all-changed', event)
}
</script>

<style lang="scss" scoped>
.hcl-toolbar {
  display: flex;
  justify-content: space-between;
  flex: 0 1 auto;
  width: 100%;
  flex-wrap: wrap;
  row-gap: 0.5rem;

  .left,
  .middle,
  .right {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1 0;
  }

  .left {
    justify-content: flex-start;
  }

  .middle {
    justify-content: center;
  }

  .right {
    justify-content: flex-end;
  }
}
</style>
